import React, { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { XCircle, ArrowLeft, CreditCard, AlertTriangle, HelpCircle } from 'lucide-react';
import GTMService from '../lib/gtm';
import { useAuth } from '../contexts/AuthContext';

export default function PaymentFailurePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const sessionId = searchParams.get('session_id');
  const errorType = searchParams.get('error_type') || 'payment_failed';
  const errorMessage = searchParams.get('error_message') || 'Your payment could not be processed';

  useEffect(() => {
    // Track payment failure event for analytics
    if (user?.id) {
      GTMService.trackError(
        `Payment failed: ${errorType} - ${errorMessage}`,
        false, // Not fatal
        user.id
      );

      // Track custom payment failure event
      GTMService.trackCustomEvent('payment_failed', {
        error_type: errorType,
        error_message: errorMessage,
        session_id: sessionId,
        page_location: window.location.href
      }, user.id);
    }
  }, [user, errorType, errorMessage, sessionId]);

  const getErrorIcon = () => {
    switch (errorType) {
      case 'card_declined':
        return <CreditCard className="w-16 h-16 text-red-500 mx-auto mb-4" />;
      case 'insufficient_funds':
        return <AlertTriangle className="w-16 h-16 text-orange-500 mx-auto mb-4" />;
      case 'authentication_required':
        return <HelpCircle className="w-16 h-16 text-blue-500 mx-auto mb-4" />;
      default:
        return <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />;
    }
  };

  const getErrorTitle = () => {
    switch (errorType) {
      case 'card_declined':
        return 'Card Declined';
      case 'insufficient_funds':
        return 'Insufficient Funds';
      case 'authentication_required':
        return 'Authentication Required';
      case 'processing_error':
        return 'Processing Error';
      default:
        return 'Payment Failed';
    }
  };

  const getErrorDescription = () => {
    switch (errorType) {
      case 'card_declined':
        return 'Your card was declined by your bank. This could be due to incorrect card details, security restrictions, or other bank policies.';
      case 'insufficient_funds':
        return 'There are insufficient funds available on your payment method to complete this transaction.';
      case 'authentication_required':
        return 'Your bank requires additional authentication to complete this payment. Please try again or use a different payment method.';
      case 'processing_error':
        return 'We encountered an error while processing your payment. This is usually temporary.';
      default:
        return 'We were unable to process your payment. This could be due to various reasons including card restrictions, network issues, or temporary service problems.';
    }
  };

  const getSuggestedActions = () => {
    switch (errorType) {
      case 'card_declined':
        return [
          'Verify your card details are correct',
          'Contact your bank to ensure the card is active',
          'Check if your card supports online payments',
          'Try a different payment method'
        ];
      case 'insufficient_funds':
        return [
          'Check your account balance',
          'Add funds to your account',
          'Try a different payment method',
          'Contact your bank for assistance'
        ];
      case 'authentication_required':
        return [
          'Try the payment again (3D Secure may appear)',
          'Ensure you complete any additional verification steps',
          'Contact your bank if authentication fails',
          'Try a different payment method'
        ];
      case 'processing_error':
        return [
          'Wait a few minutes and try again',
          'Check your internet connection',
          'Try a different payment method',
          'Contact support if the issue persists'
        ];
      default:
        return [
          'Check your card details and try again',
          'Ensure your payment method is valid and active',
          'Try a different payment method',
          'Contact support if the issue continues'
        ];
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <Helmet>
        <title>Payment Failed - Rulevia</title>
      </Helmet>
      <div className="max-w-lg w-full bg-white rounded-lg shadow-lg p-8">
        <div className="text-center">
          {getErrorIcon()}
          
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {getErrorTitle()}
          </h1>
          
          <p className="text-gray-600 mb-6">
            {getErrorDescription()}
          </p>

          {sessionId && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-6">
              <p className="text-gray-500 text-xs">
                Session ID: <span className="font-mono">{sessionId}</span>
              </p>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-blue-800 font-medium mb-3">Suggested Actions:</p>
            <ul className="text-blue-700 text-sm space-y-1">
              {getSuggestedActions().map((action, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  {action}
                </li>
              ))}
            </ul>
          </div>

          <div className="space-y-3">
            <button
              onClick={() => navigate('/onboarding?step=subscription')}
              className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              <CreditCard className="w-5 h-5" />
              Try Payment Again
            </button>
            
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-5 h-5" />
              Return to Dashboard
            </button>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-gray-500 text-sm mb-3">
              Need help resolving this issue?
            </p>
            <button
              onClick={() => {
                const subject = `Payment Failed: ${getErrorTitle()}`;
                const body = `I encountered a payment failure with the following details:\n\nError Type: ${errorType}\nError Message: ${errorMessage}\nSession ID: ${sessionId || 'N/A'}\n\nPlease help me resolve this issue.`;
                window.location.href = `mailto:support@rulevia.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
              }}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              Contact Support
            </button>
          </div>
        </div>
      </div>
    </div>
  );
} 